'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import cx from 'classnames';
import { usePmOnboardingSessionContext } from '@components/pm-onboarding-session-provider';
import { routes } from '@routes';
import { PmOnboardingSessionStatus } from '@gql';

type OnboardingStep = 'join' | 'import' | 'conduct' | 'subscribe' | 'review';

/**
 * Development-only debug panel for PM onboarding session inspection.
 * Only renders when NODE_ENV === 'development'
 */
export const PmOnboardingDebug: React.FC = () => {
  const router = useRouter();
  const [isOpen, setIsOpen] = React.useState(false);
  const { session, loading, error, refetch } = usePmOnboardingSessionContext();

  // Only render in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const handleNavigateToStep = (step: OnboardingStep) => {
    const stepRoutes: Record<OnboardingStep, string> = {
      join: routes.onboardingJoin,
      import: routes.onboardingImportListings,
      conduct: routes.onboardingCodeOfConduct,
      subscribe: routes.onboardingSubscribe,
      review: routes.onboardingReview,
    };
    router.push(stepRoutes[step]);
  };

  const handleEnablePmsTesting = () => {
    router.push(`${routes.onboardingImportListings}?pms_testing=true`);
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9999]">
      {/* Toggle Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cx(
          'w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white font-bold transition-transform',
          {
            'bg-primary-main': !isOpen,
            'bg-error-main rotate-45': isOpen,
          }
        )}
        title="Toggle Debug Panel"
      >
        {isOpen ? '×' : '🔧'}
      </button>

      {/* Panel */}
      {isOpen && (
        <div className="absolute bottom-14 right-0 w-80 max-h-[500px] overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200">
          <div className="sticky top-0 bg-primary-main text-white px-4 py-2 font-semibold">
            PM Onboarding Debug
          </div>

          <div className="p-4 space-y-4">
            {/* Quick Actions */}
            <div className="space-y-2">
              <h4 className="font-semibold text-sm text-gray-700">Actions</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="px-3 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
                >
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={handleEnablePmsTesting}
                  className="px-3 py-1 text-xs bg-secondary-main/10 text-secondary-dark rounded hover:bg-secondary-main/20"
                >
                  PMS Testing Mode
                </button>
              </div>
            </div>

            {/* Step Navigation */}
            <div className="space-y-2">
              <h4 className="font-semibold text-sm text-gray-700">
                Jump to Step
              </h4>
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    'join',
                    'import',
                    'conduct',
                    'subscribe',
                    'review',
                  ] as OnboardingStep[]
                ).map((step) => (
                  <button
                    type="button"
                    key={step}
                    onClick={() => handleNavigateToStep(step)}
                    className={cx(
                      'px-3 py-1 text-xs rounded hover:bg-primary-main/20',
                      {
                        'bg-primary-main text-white':
                          session?.currentStepKey === step,
                        'bg-primary-main/10 text-primary-main':
                          session?.currentStepKey !== step,
                      }
                    )}
                  >
                    {step}
                  </button>
                ))}
              </div>
            </div>

            {/* Session Status */}
            <div className="space-y-2">
              <h4 className="font-semibold text-sm text-gray-700">Status</h4>
              <div className="text-xs space-y-1">
                <div>
                  <span className="text-gray-500">Loading:</span>{' '}
                  <span
                    className={
                      loading ? 'text-secondary-main' : 'text-gray-900'
                    }
                  >
                    {loading ? 'Yes' : 'No'}
                  </span>
                </div>
                {error && (
                  <div className="text-error-main">Error: {error.message}</div>
                )}
                {session && (
                  <>
                    <div>
                      <span className="text-gray-500">Session ID:</span>{' '}
                      <span className="font-mono">
                        {session.id.slice(0, 8)}...
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Current Step:</span>{' '}
                      <span className="font-semibold">
                        {session.currentStepKey}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>{' '}
                      <span
                        className={cx({
                          'text-success-main':
                            session.status ===
                            PmOnboardingSessionStatus.Completed,
                          'text-secondary-main':
                            session.status ===
                            PmOnboardingSessionStatus.InProgress,
                        })}
                      >
                        {session.status}
                      </span>
                    </div>
                    {session.publishedAt && (
                      <div>
                        <span className="text-gray-500">Published:</span>{' '}
                        <span className="text-success-main font-semibold">
                          {new Date(session.publishedAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Full Session State */}
            <div className="space-y-2">
              <h4 className="font-semibold text-sm text-gray-700">
                Full Session
              </h4>
              {session ? (
                <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto max-h-48">
                  {JSON.stringify(session, null, 2)}
                </pre>
              ) : (
                <p className="text-xs text-gray-500 italic">No session found</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

PmOnboardingDebug.displayName = 'PmOnboardingDebug';
